import React, { useMemo, useState, useEffect, useCallback, useRef, memo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Mail, TrendingUp, AlertTriangle, FileText, Download, Upload, Search, Filter, Calendar, RefreshCw, Eye, Zap, Settings, Users, Clock, Target, ArrowUp, ArrowDown, Activity, Menu, X, Loader2 } from "lucide-react";

// Generate enhanced dummy data for performance testing
function generateLargeDummyData(count = 1200) {
  const baseData = [
    {
      email_id: "e1",
      subject: "Initial Review - Case Assignment Notification",
      body: "Your immigration case has been assigned to Immigration Judge Patricia Wong for review. Please ensure all documentation is submitted within the required timeframe. Your first hearing is scheduled for December 15, 2023, at 9:00 AM in Courtroom 7.",
      attachment_content: "Case Assignment Order - Case #A123456789 - Assigned Judge: Patricia Wong",
      prediction_category_value: "EOIR Notification",
      prediction_order_type_value: "N/A",
      prediction_priority_value: "Normal",
      classification_timestamp: new Date("2023-11-05 09:15:00"),
      sender_email: "assignments@immigration.court.gov",
      recipient_count: 2,
      case_number: "A123456789",
      hearing_date: "2023-12-15",
      name: "John Smith"
    },
    {
      email_id: "e2",
      subject: "Document Submission Deadline - 30 Days Remaining",
      body: "This is a reminder that you have 30 days remaining to submit all required documentation for your asylum case. Please ensure all evidence, witness statements, and country condition reports are filed before the deadline.",
      attachment_content: "Document Reminder - Case #A987654321 - Deadline: December 10, 2023",
      prediction_category_value: "EOIR Notification",
      prediction_order_type_value: "N/A",
      prediction_priority_value: "High",
      classification_timestamp: new Date("2023-11-10 14:30:00"),
      sender_email: "filings@immigration.court.gov",
      recipient_count: 1,
      case_number: "A987654321",
      hearing_date: "2023-12-10",
      name: "Maria Garcia"
    },
    {
      email_id: "e3",
      subject: "Pre-hearing Conference Scheduled",
      body: "A pre-hearing conference has been scheduled for your case on November 28, 2023, at 2:00 PM. This conference will address procedural matters and evidence submission.",
      attachment_content: "Pre-hearing Conference Notice - Case #A456789123 - Date: November 28, 2023",
      prediction_category_value: "EOIR Notification",
      prediction_order_type_value: "N/A",
      prediction_priority_value: "Normal",
      classification_timestamp: new Date("2023-11-20 11:45:00"),
      sender_email: "scheduling@immigration.court.gov",
      recipient_count: 3,
      case_number: "A456789123",
      hearing_date: "2023-11-28",
      name: "Ahmed Hassan"
    },
    {
      email_id: "e4",
      subject: "Case Update - Final Decision Rendered",
      body: "This email contains important information regarding the final decision in your immigration case. The Immigration Judge has reviewed all evidence and testimony presented during your hearing on December 15, 2023.",
      attachment_content: "Final Order Document - Case #A123456789 - Decision: Order of Removal",
      prediction_category_value: "Final Orders",
      prediction_order_type_value: "Order of Removal",
      prediction_priority_value: "High",
      classification_timestamp: new Date("2023-12-20 09:15:00"),
      sender_email: "clerk@immigration.court.gov",
      recipient_count: 3,
      case_number: "A123456789",
      hearing_date: "2023-12-15",
      name: "John Smith"
    }
  ];

  const generatedData = [];
  for (let i = 0; i < count; i++) {
    const baseItem = baseData[i % baseData.length];
    const timestamp = new Date();
    timestamp.setDate(timestamp.getDate() - Math.floor(Math.random() * 90));
    timestamp.setHours(Math.floor(Math.random() * 24));
    timestamp.setMinutes(Math.floor(Math.random() * 60));
    
    generatedData.push({
      ...baseItem,
      email_id: `e${1000 + i}`,
      case_number: `A${Math.floor(100000000 + Math.random() * 900000000)}`,
      classification_timestamp: timestamp,
      name: `${['John', 'Maria', 'Ahmed', 'Carlos', 'Sarah', 'David', 'Lisa', 'Michael'][i % 8]} ${['Smith', 'Garcia', 'Hassan', 'Rodriguez', 'Johnson', 'Williams', 'Brown', 'Davis'][i % 8]}`,
      prediction_priority_value: Math.random() > 0.7 ? "High" : Math.random() > 0.4 ? "Normal" : "Low",
      recipient_count: Math.floor(Math.random() * 5) + 1
    });
  }
  return generatedData;
}

// Helper Functions
function safeDate(d) {
  if (!d) return null;
  try {
    const date = new Date(d);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

function formatDate(dateString, format = "YYYY-MM-DD HH:mm:ss") {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    
    const formatMap = {
      "YYYY-MM-DD HH:mm:ss": () => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      },
      "YYYY-MM-DD HH:mm": () => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
      "YYYY-MM-DD": () => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      },
      "YYYY-MM": () => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
      },
      "HH:mm:ss": () => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      },
      "MM-DD HH:mm": () => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}-${day} ${hours}:${minutes}`;
      },
      "YYYY": () => date.getFullYear().toString()
    };

    return formatMap[format] ? formatMap[format]() : date.toLocaleString();
  } catch {
    return "-";
  }
}

function exportToCSV(data, filename = 'email_data.csv') {
  const headers = ['Email ID', 'Subject', 'Name', 'Case Number', 'Priority', 'Category', 'Date', 'Sender'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      row.email_id || '',
      `"${(row.subject || '').replace(/"/g, '""')}"`,
      row.name || 'N/A',
      row.case_number || 'N/A',
      row.prediction_priority_value || 'Normal',
      `"${(row.prediction_category_value || '').replace(/"/g, '""')}"`,
      row.classification_timestamp ? formatDate(row.classification_timestamp) : '-',
      row.sender_email || 'N/A'
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// Memoized Components
const MetricCard = memo(({ title, value, icon, onClick, style, subtitle }) => (
  <div style={{ ...cardStyle, ...style, cursor: 'pointer' }} onClick={onClick}>
    <div style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
        {icon}
      </div>
      <h3 style={{ fontSize: '1.75rem', color: '#ffffff', fontWeight: 'bold', margin: '0 0 0.5rem 0', lineHeight: '1', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
        {value.toLocaleString()}
      </h3>
      <p style={{ fontSize: '0.875rem', fontWeight: '600', margin: 0, textAlign: 'center', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)', opacity: 0.9 }}>
        {subtitle}
      </p>
    </div>
  </div>
));

const ChartCard = memo(({ title, icon, children, headerStyle: customHeaderStyle }) => (
  <div style={cardStyle}>
    <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(71, 85, 105, 0.3)', ...customHeaderStyle }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {icon}
        <h4 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.25rem', fontWeight: '700', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>
          {title}
        </h4>
      </div>
    </div>
    <div style={{ padding: '1.5rem' }}>
      {children}
    </div>
  </div>
));

const LoadingSpinner = memo(() => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
    <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#3b82f6" />
    <span style={{ marginLeft: '0.75rem', color: '#94a3b8', fontSize: '1rem' }}>Loading...</span>
  </div>
));

const EmailDetail = memo(({ email, onClose }) => {
  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={{ ...modalContentStyle, maxWidth: '95vw', maxHeight: '95vh' }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                <Mail size={24} color="#3b82f6" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>Email Details</h3>
                <p style={{ fontSize: '1rem', color: '#6b7280', fontFamily: 'monospace', margin: '0.5rem 0 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {email.email_id}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexShrink: 0 }}>
              <button style={{ ...closeButtonStyle, fontSize: '1.25rem' }} onClick={onClose}>
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
        <div style={{ ...modalBodyStyle, maxHeight: '80vh', padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ ...labelStyle, fontSize: '1.125rem' }}>Subject</label>
            <p style={{ color: '#6b7280', fontWeight: '500', fontSize: '1.125rem', lineHeight: '1.6' }}>{email.subject}</p>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ ...labelStyle, fontSize: '1.125rem' }}>Email Body</label>
            <div style={{ ...bodyTextStyle, maxHeight: '8rem', fontSize: '1rem', lineHeight: '1.6' }}>
              {email.body}
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ ...labelStyle, fontSize: '1rem' }}>Name</label>
              <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>{email.name || "Unknown"}</p>
            </div>
            
            <div>
              <label style={{ ...labelStyle, fontSize: '1rem' }}>A Number</label>
              <p style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '1.125rem' }}>{email.case_number || "N/A"}</p>
            </div>
            
            <div>
              <label style={{ ...labelStyle, fontSize: '1rem' }}>Priority</label>
              <p style={{
                fontWeight: '600',
                fontSize: '1.125rem',
                color: email.prediction_priority_value === "High" 
                  ? "#dc2626" 
                  : email.prediction_priority_value === "Normal" 
                  ? "#059669" 
                  : "#6b7280"
              }}>
                {email.prediction_priority_value}
              </p>
            </div>
            
            <div>
              <label style={{ ...labelStyle, fontSize: '1rem' }}>Email Received</label>
              <p style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '1rem' }}>
                {email.classification_timestamp 
                  ? formatDate(email.classification_timestamp, "YYYY-MM-DD HH:mm")
                  : "-"
                }
              </p>
            </div>
          </div>

          {email.attachment_content && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ ...labelStyle, fontSize: '1.125rem' }}>Attachment Details</label>
              <div style={{ ...bodyTextStyle, maxHeight: '6rem', fontSize: '1rem', lineHeight: '1.6' }}>
                {email.attachment_content}
              </div>
            </div>
          )}
          
          <div>
            <label style={{ ...labelStyle, fontSize: '1.125rem' }}>Classification Timestamp</label>
            <p style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: '1rem' }}>
              {email.classification_timestamp 
                ? formatDate(email.classification_timestamp)
                : "-"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

// Main Enhanced Component
export default function EnhancedEmailClassificationDashboard() {
  const [data, setData] = useState(() => 
    generateLargeDummyData(1200).map((r) => ({
      ...r,
      classification_timestamp: safeDate(r.classification_timestamp)
    }))
  );
  
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedDetailCard, setSelectedDetailCard] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRealtime, setIsRealtime] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [isConnectedToLive, setIsConnectedToLive] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // WebSocket simulation
  const wsRef = useRef(null);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current) clearInterval(wsRef.current);
    
    const simulateRealTimeData = () => {
      const baseData = generateLargeDummyData(4);
      const newEmail = {
        ...baseData[Math.floor(Math.random() * baseData.length)],
        email_id: `e${Date.now()}`,
        classification_timestamp: new Date(),
        recipient_count: Math.floor(Math.random() * 5) + 1,
        prediction_priority_value: Math.random() > 0.7 ? "High" : Math.random() > 0.5 ? "Normal" : "Low"
      };
      
      setData(prevData => {
        const updatedData = [newEmail, ...prevData].slice(0, 1500);
        return updatedData;
      });
      setLastUpdated(new Date());
      setRefreshCount(prev => prev + 1);
    };

    if (isRealtime) {
      wsRef.current = window.setInterval(simulateRealTimeData, 8000);
    }

    return () => {
      if (wsRef.current) clearInterval(wsRef.current);
    };
  }, [isRealtime]);

  useEffect(() => {
    const cleanup = connectWebSocket();
    return cleanup;
  }, [connectWebSocket]);

  // Real-time clock update
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleEmailClick = useCallback((email) => {
    setSelectedEmail(email);
  }, []);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      exportToCSV(filteredData, `email_classification_${formatDate(new Date(), "YYYY-MM-DD")}.csv`);
      await new Promise(resolve => setTimeout(resolve, 1000));
    } finally {
      setIsExporting(false);
    }
  }, []);

  const connectToLiveEndpoint = useCallback(async () => {
    if (!apiEndpoint.trim()) {
      alert('Please enter an API endpoint URL');
      return;
    }

    try {
      setConnectionStatus('connecting');
      setIsLoading(true);
      
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        cache: 'no-cache'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      const jsonData = await response.json();
      
      let emailData = [];
      if (Array.isArray(jsonData)) {
        emailData = jsonData;
      } else if (jsonData.data && Array.isArray(jsonData.data)) {
        emailData = jsonData.data;
      } else if (jsonData.emails && Array.isArray(jsonData.emails)) {
        emailData = jsonData.emails;
      } else {
        throw new Error('Invalid response format. Expected array of emails.');
      }

      const normalized = emailData.map((r) => ({
        ...r,
        classification_timestamp: safeDate(r.classification_timestamp || r.timestamp || r.created_at)
      }));

      setData(normalized);
      setLastUpdated(new Date());
      setIsConnectedToLive(true);
      setConnectionStatus('connected');
      alert(`Successfully connected! Loaded ${normalized.length} emails.`);
    } catch (error) {
      console.error('Connection error:', error);
      setConnectionStatus('error');
      setIsConnectedToLive(false);
      alert(`Failed to connect: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [apiEndpoint]);

  const disconnectFromEndpoint = useCallback(() => {
    setIsConnectedToLive(false);
    setConnectionStatus('disconnected');
    setData(generateLargeDummyData(1200).map((r) => ({ ...r, classification_timestamp: safeDate(r.classification_timestamp) })));
    alert('Disconnected from live endpoint. Using sample data.');
  }, []);

  // Get unique months from data
  const availableMonths = useMemo(() => {
    const months = new Set();
    data.forEach((r) => {
      const dt = safeDate(r.classification_timestamp);
      if (dt) {
        const monthKey = formatDate(dt, "YYYY-MM");
        months.add(monthKey);
      }
    });
    return Array.from(months).sort().reverse();
  }, [data]);

  // Performance-optimized filtering for large datasets
  const filteredData = useMemo(() => {
    let filtered = data;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        if (r.email_id && r.email_id.toLowerCase().includes(term)) return true;
        if (r.case_number && r.case_number.toLowerCase().includes(term)) return true;
        if (r.subject && r.subject.toLowerCase().includes(term)) return true;
        if (r.name && r.name.toLowerCase().includes(term)) return true;
        if (r.sender_email && r.sender_email.toLowerCase().includes(term)) return true;
        return false;
      });
    }
    
    if (viewMode === 'monthly' && selectedMonth) {
      filtered = filtered.filter((r) => {
        const dt = safeDate(r.classification_timestamp);
        if (!dt) return false;
        const monthKey = formatDate(dt, "YYYY-MM");
        return monthKey === selectedMonth;
      });
    }
    
    if (selectedPriority) {
      filtered = filtered.filter((r) => r.prediction_priority_value === selectedPriority);
    }
    
    if (selectedCategory) {
      filtered = filtered.filter((r) => r.prediction_category_value === selectedCategory);
    }
    
    if (selectedOrderType) {
      filtered = filtered.filter((r) => r.prediction_order_type_value === selectedOrderType);
    }
    
    if (selectedSubject) {
      filtered = filtered.filter((r) => {
        const subject = r.subject || "";
        switch (selectedSubject) {
          case "Case Assignments":
            return subject.toLowerCase().includes('assignment') || subject.toLowerCase().includes('assigned');
          case "Deadlines & Reminders":
            return subject.toLowerCase().includes('deadline') || subject.toLowerCase().includes('reminder');
          case "Hearings & Conferences":
            return subject.toLowerCase().includes('hearing') || subject.toLowerCase().includes('conference');
          case "Decisions & Orders":
            return subject.toLowerCase().includes('decision') || subject.toLowerCase().includes('order');
          case "Document Submissions":
            return subject.toLowerCase().includes('document') || subject.toLowerCase().includes('filing');
          case "Notices & Notifications":
            return subject.toLowerCase().includes('notice') || subject.toLowerCase().includes('notification');
          case "Status Updates":
            return subject.toLowerCase().includes('update') || subject.toLowerCase().includes('status');
          case "Appeals & Motions":
            return subject.toLowerCase().includes('appeal') || subject.toLowerCase().includes('motion');
          case "Other Communications":
            return !['assignment', 'assigned', 'deadline', 'reminder', 'hearing', 'conference', 'decision', 'order', 'document', 'filing', 'notice', 'notification', 'update', 'status', 'appeal', 'motion'].some(keyword => subject.toLowerCase().includes(keyword)) && subject !== "";
          default:
            return true;
        }
      });
    }
    
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter((r) => {
        const dt = safeDate(r.classification_timestamp);
        if (!dt) return false;
        
        const dateStr = formatDate(dt, "YYYY-MM-DD");
        const inRange = (!dateRange.start || dateStr >= dateRange.start) && 
                       (!dateRange.end || dateStr <= dateRange.end);
        return inRange;
      });
    }
    
    return filtered;
  }, [data, viewMode, selectedMonth, selectedPriority, searchTerm, dateRange, selectedSubject, selectedCategory, selectedOrderType]);

  // Enhanced metrics calculations
  const enhancedMetrics = useMemo(() => {
    const total = filteredData.length;
    const highPriority = filteredData.filter(d => d.prediction_priority_value === "High").length;
    const activeCases = filteredData.filter(d => d.case_number).length;
    const todayCount = filteredData.filter(d => {
      const dt = safeDate(d.classification_timestamp);
      if (!dt) return false;
      const today = new Date();
      return dt.toDateString() === today.toDateString();
    }).length;

    return {
      total,
      highPriority,
      activeCases,
      todayCount
    };
  }, [filteredData]);

  // Chart data calculations
  const categoryCounts = useMemo(() => {
    const map = new Map();
    filteredData.forEach((r) => {
      const key = r.prediction_category_value || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(([Category, Count]) => ({ Category, Count }));
  }, [filteredData]);

  const subjectCounts = useMemo(() => {
    const map = new Map();
    filteredData.forEach((r) => {
      const subject = r.subject || "Unknown";
      let key = "Other";
      
      if (subject.toLowerCase().includes('assignment') || subject.toLowerCase().includes('assigned')) {
        key = "Case Assignments";
      } else if (subject.toLowerCase().includes('deadline') || subject.toLowerCase().includes('reminder')) {
        key = "Deadlines & Reminders";
      } else if (subject.toLowerCase().includes('hearing') || subject.toLowerCase().includes('conference')) {
        key = "Hearings & Conferences";
      } else if (subject.toLowerCase().includes('decision') || subject.toLowerCase().includes('order')) {
        key = "Decisions & Orders";
      } else if (subject.toLowerCase().includes('document') || subject.toLowerCase().includes('filing')) {
        key = "Document Submissions";
      } else if (subject.toLowerCase().includes('notice') || subject.toLowerCase().includes('notification')) {
        key = "Notices & Notifications";
      } else if (subject.toLowerCase().includes('update') || subject.toLowerCase().includes('status')) {
        key = "Status Updates";
      } else if (subject.toLowerCase().includes('appeal') || subject.toLowerCase().includes('motion')) {
        key = "Appeals & Motions";
      } else if (subject !== "Unknown") {
        key = "Other Communications";
      }
      
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(([Subject, Count]) => ({ Subject, Count }));
  }, [filteredData]);

  const priorityCounts = useMemo(() => {
    const map = new Map();
    filteredData.forEach((r) => {
      const key = r.prediction_priority_value || "Normal";
      map.set(key, (map.get(key) || 0) + 1);
    });
    const order = ["High", "Normal", "Low"];
    return Array.from(map.entries())
      .map(([Priority, Count]) => ({ Priority, Count }))
      .sort((a, b) => order.indexOf(a.Priority) - order.indexOf(b.Priority));
  }, [filteredData]);

  const orderTypeCounts = useMemo(() => {
    const map = new Map();
    filteredData.forEach((r) => {
      const key = r.prediction_order_type_value || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(([OrderType, Count]) => ({ OrderType, Count }));
  }, [filteredData]);

  const timeSeries = useMemo(() => {
    const map = new Map();
    filteredData.forEach((r) => {
      const dt = safeDate(r.classification_timestamp);
      let key = "Unknown";
      if (dt) {
        switch (viewMode) {
          case 'hourly':
            key = formatDate(dt, "YYYY-MM-DD HH:mm");
            break;
          case 'daily':
            key = formatDate(dt, "YYYY-MM-DD");
            break;
          case 'monthly':
            key = formatDate(dt, "YYYY-MM");
            break;
          case 'quarterly':
            const year = dt.getFullYear();
            const quarter = Math.floor(dt.getMonth() / 3) + 1;
            key = `${year}-Q${quarter}`;
            break;
          case 'yearly':
            key = formatDate(dt, "YYYY");
            break;
          default:
            key = formatDate(dt, "YYYY-MM-DD");
        }
      }
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([Date, Count]) => ({ Date, Count }))
      .sort((a, b) => (a.Date > b.Date ? 1 : -1));
  }, [filteredData, viewMode]);

  return (
    <div style={containerStyle}>
      <style>{mobileResponsiveCssStyles}</style>
      
      {/* Enhanced Header */}
      <header style={headerStyle}>
        <div style={headerContentStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
              <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                <Mail size={28} color="#3b82f6" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={titleStyle}>
                  IMS Dashboard
                </h1>
                <p style={{ color: '#94a3b8', margin: 0, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Immigration email classification dashboard
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
              <div style={{ ...statusStyle, padding: '0.5rem 1rem', fontSize: '1rem' }}>
                <div style={statusDotStyle}></div>
                <span className="status-text">
                  {isRealtime ? `Live • ${formatDate(currentTime, "HH:mm:ss")}` : 'Offline'}
                </span>
              </div>
              <button
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '0.75rem'
                }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            borderTop: '1px solid rgba(71, 85, 105, 0.3)',
            padding: '1.5rem',
            backdropFilter: 'blur(16px)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                style={isRealtime ? { ...primaryButtonStyle, width: '100%', fontSize: '1rem' } : { ...outlineButtonStyle, width: '100%', fontSize: '1rem' }}
                onClick={() => {
                  setIsRealtime(!isRealtime);
                  setIsMobileMenuOpen(false);
                }}
              >
                <Zap size={20} style={{ marginRight: '0.5rem' }} />
                {isRealtime ? 'Live Mode ON' : 'Live Mode OFF'}
              </button>
              <button
                style={{ ...outlineButtonStyle, width: '100%', fontSize: '1rem' }}
                onClick={() => {
                  handleExport();
                  setIsMobileMenuOpen(false);
                }}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 size={20} style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Download size={20} style={{ marginRight: '0.5rem' }} />
                )}
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <div style={{ fontSize: '1rem', color: '#94a3b8', textAlign: 'center', padding: '0.75rem' }}>
                Updates: {refreshCount} | Status: {connectionStatus.toUpperCase()} | Total: {data.length.toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ padding: '1.5rem' }}>
        {/* Live API Endpoint Configuration */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <div style={{ ...cardHeaderStyle, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Upload size={22} color="#3b82f6" />
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
                Live Email Feed
              </h3>
              <div style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                background: connectionStatus === 'connected' ? '#f0fdf4' : 
                           connectionStatus === 'connecting' ? '#fef3c7' : 
                           connectionStatus === 'error' ? '#fef2f2' : '#f8fafc',
                border: '1px solid',
                borderColor: connectionStatus === 'connected' ? '#bbf7d0' : 
                            connectionStatus === 'connecting' ? '#fed7aa' : 
                            connectionStatus === 'error' ? '#fecaca' : '#e2e8f0'
              }}>
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  color: connectionStatus === 'connected' ? '#065f46' : 
                         connectionStatus === 'connecting' ? '#92400e' : 
                         connectionStatus === 'error' ? '#991b1b' : '#374151'
                }}>
                  {connectionStatus === 'connected' ? 'LIVE' : 
                   connectionStatus === 'connecting' ? 'CONNECTING' : 
                   connectionStatus === 'error' ? 'ERROR' : 'OFFLINE'}
                </span>
              </div>
            </div>
          </div>
          
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <input
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
                placeholder="https://your-api.com/emails/live"
                style={{ ...inputStyle, flex: 1, fontSize: '1rem' }}
                onKeyPress={(e) => e.key === 'Enter' && !isConnectedToLive && !isLoading && connectToLiveEndpoint()}
              />
              <button 
                style={isConnectedToLive ? { ...primaryButtonStyle, background: 'linear-gradient(to right, #dc2626, #b91c1c)', fontSize: '1rem' } : { ...primaryButtonStyle, fontSize: '1rem' }}
                onClick={isConnectedToLive ? disconnectFromEndpoint : connectToLiveEndpoint}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={20} style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                    Connecting...
                  </>
                ) : isConnectedToLive ? (
                  <>
                    <X size={20} style={{ marginRight: '0.5rem' }} />
                    Disconnect
                  </>
                ) : (
                  <>
                    <Upload size={20} style={{ marginRight: '0.5rem' }} />
                    Connect
                  </>
                )}
              </button>
            </div>

            <div style={{ padding: '1rem', background: 'rgba(30, 41, 59, 0.6)', borderRadius: '0.75rem', fontSize: '1rem', color: '#94a3b8', backdropFilter: 'blur(8px)', border: '1px solid rgba(71, 85, 105, 0.3)' }}>
              <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#f1f5f9' }}>API Requirements:</div>
              <div>• Method: GET</div>
              <div>• Response: JSON array of email objects</div>
              <div>• Required fields: email_id, subject, prediction_category_value, prediction_priority_value</div>
              <div>• Optional: classification_timestamp, case_number, sender_email</div>
            </div>
          </div>
        </div>

        {/* Search & Filter Section */}
        <div style={{ ...cardStyle, marginBottom: '1.5rem' }}>
          <div style={{ padding: '1.5rem' }}>
            <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                style={{ ...selectStyle, fontSize: '1rem' }}
              >
                <option value="">All Priorities</option>
                <option value="High">High</option>
                <option value="Normal">Normal</option>
                <option value="Low">Low</option>
              </select>
              
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                style={{ ...selectStyle, fontSize: '1rem' }}
              >
                <option value="all">All Time</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>

              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{ ...selectStyle, fontSize: '1rem' }}
              >
                <option value="">All Subject Types</option>
                {subjectCounts.map(({ Subject }) => (
                  <option key={Subject} value={Subject}>{Subject}</option>
                ))}
              </select>
            </div>
            
            {(searchTerm || selectedPriority || selectedCategory || selectedOrderType || selectedSubject || dateRange.start || dateRange.end || selectedMonth) && (
              <button
                style={{ ...outlineButtonStyle, width: '100%', marginTop: '1rem', fontSize: '1rem' }}
                onClick={() => {
                  setSearchTerm('');
                  setSelectedPriority('');
                  setSelectedCategory('');
                  setSelectedOrderType('');
                  setSelectedSubject('');
                  setDateRange({ start: '', end: '' });
                  setSelectedMonth('');
                  setViewMode('all');
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        {/* Metric Cards */}
        <div className="metric-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <MetricCard
            title="Total Emails"
            value={enhancedMetrics.total}
            icon={<Mail size={24} color="#ffffff" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />}
            onClick={() => setSelectedDetailCard('total')}
            style={metricCardBlue}
            subtitle="Total Emails"
          />
          
          <MetricCard
            title="High Priority"
            value={enhancedMetrics.highPriority}
            icon={<AlertTriangle size={24} color="#ffffff" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />}
            onClick={() => setSelectedDetailCard('high-priority')}
            style={metricCardRed}
            subtitle="High Priority"
          />
          
          <MetricCard
            title="Active Cases"
            value={enhancedMetrics.activeCases}
            icon={<Users size={24} color="#ffffff" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />}
            onClick={() => setSelectedDetailCard('active-cases')}
            style={metricCardGreen}
            subtitle="Active Cases"
          />
          
          <MetricCard
            title="Today"
            value={enhancedMetrics.todayCount}
            icon={<Clock size={24} color="#ffffff" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }} />}
            onClick={() => setSelectedDetailCard('today')}
            style={metricCardPurple}
            subtitle="Today"
          />
        </div>

        {/* Charts Section */}
        <div className="charts-section" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1.5rem', 
          marginBottom: '2rem' 
        }}>
          {/* Subject Bar Chart */}
          <ChartCard
            title="Emails by Subject Type"
            icon={<FileText size={22} color="#34d399" />}
            headerStyle={{ background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.2) 0%, rgba(16, 185, 129, 0.15) 100%)' }}
          >
            <div style={{ height: '28rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={subjectCounts} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  onClick={(data) => {
                    if (data && data.activeLabel) {
                      setSelectedSubject(data.activeLabel);
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(241, 245, 249, 0.3)" />
                  <XAxis 
                    dataKey="Subject" 
                    fontSize={12}
                    tick={{ fill: '#f1f5f9', fontWeight: '500', cursor: 'pointer' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis 
                    tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}k` : value.toLocaleString()}
                    fontSize={14}
                    tick={{ fill: '#f1f5f9', fontWeight: '500' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
                      fontSize: '12px',
                      padding: '8px'
                    }}
                    formatter={(value, name) => [`${value.toLocaleString()}`, 'Count']}
                    labelFormatter={(label) => `Click to filter: ${label}`}
                    labelStyle={{ color: '#374151', fontWeight: '500', fontSize: '12px' }}
                  />
                  <Bar 
                    dataKey="Count" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data) => {
                      if (data && data.Subject) {
                        setSelectedSubject(data.Subject);
                      }
                    }}
                  >
                    {subjectCounts.map((entry, index) => (
                      <Cell key={`subject-cell-${index}`} fill="#10b981" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          {/* Priority Pie Chart */}
          <ChartCard
            title="Emails by Priority"
            icon={<AlertTriangle size={22} color="#f87171" />}
            headerStyle={{ background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)' }}
          >
            <div style={{ height: '28rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityCounts}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    innerRadius={50}
                    paddingAngle={3}
                    dataKey="Count"
                    stroke="#ffffff"
                    strokeWidth={2}
                    onClick={(data) => {
                      if (data && data.Priority) {
                        setSelectedPriority(data.Priority);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {priorityCounts.map((entry, index) => (
                      <Cell 
                        key={`priority-cell-${index}`} 
                        fill={
                          entry.Priority === 'High' ? '#dc2626' :
                          entry.Priority === 'Normal' ? '#10b981' :
                          '#6b7280'
                        } 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
                      fontSize: '12px',
                      padding: '8px'
                    }}
                    formatter={(value, name) => [`${value.toLocaleString()}`, 'Count']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Priority Legend */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1rem' }}>
              {priorityCounts.map((entry, index) => (
                <div key={`legend-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '0.75rem',
                    height: '0.75rem',
                    borderRadius: '50%',
                    background: entry.Priority === 'High' ? '#dc2626' :
                               entry.Priority === 'Normal' ? '#10b981' :
                               '#6b7280'
                  }} />
                  <span style={{ fontSize: '1.125rem', color: '#f1f5f9', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>
                    {entry.Priority} ({entry.Count})
                  </span>
                </div>
              ))}
            </div>
          </ChartCard>

          {/* Timeline Chart */}
          <div className="timeline-chart" style={{ gridColumn: '1 / -1' }}>
            <ChartCard
              title="Classification Timeline"
              icon={<Activity size={22} color="#a78bfa" />}
              headerStyle={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.15) 100%)' }}
            >
              <div style={{ height: '28rem' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={timeSeries.slice(-30)} 
                    margin={{ top: 20, right: 30, left: 30, bottom: 50 }}
                    onClick={(data) => {
                      if (data && data.activeLabel) {
                        const clickedDate = data.activeLabel;
                        if (clickedDate.includes('-Q')) {
                          const [year, quarter] = clickedDate.split('-Q');
                          const startMonth = (parseInt(quarter) - 1) * 3 + 1;
                          const endMonth = startMonth + 2;
                          setDateRange({
                            start: `${year}-${String(startMonth).padStart(2, '0')}-01`,
                            end: `${year}-${String(endMonth).padStart(2, '0')}-31`
                          });
                        } else if (clickedDate.match(/^\d{4}-\d{2}$/)) {
                          setSelectedMonth(clickedDate);
                          setViewMode('monthly');
                        } else if (clickedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          setDateRange({
                            start: clickedDate,
                            end: clickedDate
                          });
                          setViewMode('daily');
                        } else if (clickedDate.match(/^\d{4}$/)) {
                          setDateRange({
                            start: `${clickedDate}-01-01`,
                            end: `${clickedDate}-12-31`
                          });
                          setViewMode('yearly');
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(241, 245, 249, 0.3)" />
                    <XAxis 
                      dataKey="Date" 
                      fontSize={14}
                      tick={{ fill: '#f1f5f9', fontWeight: '500', cursor: 'pointer' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}k` : value.toLocaleString()}
                      fontSize={14}
                      tick={{ fill: '#f1f5f9', fontWeight: '500' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #d1d5db', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px',
                        padding: '8px'
                      }}
                      formatter={(value, name) => [`${value.toLocaleString()}`, 'Count']}
                      labelFormatter={(label) => `Click to filter by: ${label}`}
                      labelStyle={{ color: '#374151', fontWeight: '500', fontSize: '12px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="Count" 
                      stroke="#8b5cf6" 
                      strokeWidth={3} 
                      dot={{ r: 4, fill: '#8b5cf6', strokeWidth: 1, stroke: '#ffffff', cursor: 'pointer' }} 
                      activeDot={{ r: 6, fill: '#7c3aed', strokeWidth: 2, stroke: '#ffffff', cursor: 'pointer' }} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>
        </div>

        {/* Data Table */}
        <div style={cardStyle}>
          <div style={{ ...chartHeaderBlue, padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.75rem', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>
                  <FileText size={22} color="#60a5fa" />
                  Email Data
                  <span style={{ background: 'rgba(59, 130, 246, 0.3)', color: '#dbeafe', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: '600', backdropFilter: 'blur(8px)', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
                    {filteredData.length.toLocaleString()}
                  </span>
                </h3>
                <p style={{ color: '#94a3b8', fontSize: '1rem', margin: '0.75rem 0 0 0' }}>
                  Tap Email ID for details
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search table..."
                    style={{ 
                      ...inputStyle, 
                      paddingLeft: '2.5rem', 
                      fontSize: '0.875rem',
                      minHeight: '36px',
                      width: '300px'
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.875rem', color: '#cbd5e1', background: 'rgba(51, 65, 85, 0.6)', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', backdropFilter: 'blur(8px)', border: '1px solid rgba(71, 85, 105, 0.3)', whiteSpace: 'nowrap' }}>
                  {Math.min(20, filteredData.length)} of {filteredData.length.toLocaleString()}
                  {filteredData.length > 1000 && (
                    <span style={{ color: '#34d399', marginLeft: '0.5rem' }}>
                      ({Math.floor(filteredData.length/1000)}k+)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '700px' }}>
              <table style={tableStyle}>
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>Email ID</th>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>Subject</th>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>Name</th>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>A Number</th>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>Priority</th>
                    <th style={{ ...thStyle, fontSize: '1rem', padding: '1rem 0.75rem' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.slice(0, 20).map((row, idx) => (
                    <tr key={row.email_id || idx} style={enhancedTableRowStyle}>
                      <td style={{ ...tdStyle, padding: '1rem 0.75rem', fontSize: '1rem' }}>
                        <button
                          style={{ ...linkButtonStyle, fontSize: '1rem' }}
                          onClick={() => handleEmailClick(row)}
                        >
                          {row.email_id}
                        </button>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '500', color: '#f1f5f9', maxWidth: '250px', padding: '1rem 0.75rem', fontSize: '1rem' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.subject}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: '#94a3b8', padding: '1rem 0.75rem', fontSize: '1rem' }}>{row.name || "Unknown"}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#60a5fa', fontWeight: '500', padding: '1rem 0.75rem', fontSize: '1rem' }}>{row.case_number || "N/A"}</td>
                      <td style={{ ...tdStyle, padding: '1rem 0.75rem' }}>
                        <span style={
                          row.prediction_priority_value === "High"
                            ? { ...priorityHighStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                            : row.prediction_priority_value === "Normal"
                            ? { ...priorityNormalStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                            : { ...priorityLowStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                        }>
                          {row.prediction_priority_value === "High" && <AlertTriangle size={12} style={{ marginRight: '0.25rem' }} />}
                          {row.prediction_priority_value}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: '0.875rem', color: '#94a3b8', fontFamily: 'monospace', padding: '1rem 0.75rem' }}>
                        {row.classification_timestamp
                          ? formatDate(row.classification_timestamp, "MM-DD HH:mm")
                          : "-"
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Metric Detail Modal */}
      {selectedDetailCard && (
        <div style={modalOverlayStyle} onClick={() => setSelectedDetailCard(null)}>
          <div style={{ ...modalContentStyle, maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ padding: '0.75rem', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.75rem' }}>
                    {selectedDetailCard === 'total' && <Mail size={24} color="#3b82f6" />}
                    {selectedDetailCard === 'high-priority' && <AlertTriangle size={24} color="#dc2626" />}
                    {selectedDetailCard === 'active-cases' && <Users size={24} color="#059669" />}
                    {selectedDetailCard === 'today' && <Clock size={24} color="#8b5cf6" />}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
                      {selectedDetailCard === 'total' && 'All Emails'}
                      {selectedDetailCard === 'high-priority' && 'High Priority Emails'}
                      {selectedDetailCard === 'active-cases' && 'Active Cases'}
                      {selectedDetailCard === 'today' && 'Today\'s Emails'}
                    </h3>
                    <p style={{ fontSize: '1.125rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                      {selectedDetailCard === 'total' && `${filteredData.length.toLocaleString()} total emails`}
                      {selectedDetailCard === 'high-priority' && `${enhancedMetrics.highPriority.toLocaleString()} high priority emails`}
                      {selectedDetailCard === 'active-cases' && `${enhancedMetrics.activeCases.toLocaleString()} emails with case numbers`}
                      {selectedDetailCard === 'today' && `${enhancedMetrics.todayCount.toLocaleString()} emails processed today`}
                    </p>
                  </div>
                </div>
                <button style={{ ...closeButtonStyle, fontSize: '1.5rem' }} onClick={() => setSelectedDetailCard(null)}>
                  <X size={20} />
                </button>
              </div>
            </div>
            <div style={{ ...modalBodyStyle, maxHeight: '70vh', padding: '1.5rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {(() => {
                  let displayData = [];
                  if (selectedDetailCard === 'total') {
                    displayData = filteredData;
                  } else if (selectedDetailCard === 'high-priority') {
                    displayData = filteredData.filter(d => d.prediction_priority_value === "High");
                  } else if (selectedDetailCard === 'active-cases') {
                    displayData = filteredData.filter(d => d.case_number);
                  } else if (selectedDetailCard === 'today') {
                    displayData = filteredData.filter(d => {
                      const dt = safeDate(d.classification_timestamp);
                      if (!dt) return false;
                      const today = new Date();
                      return dt.toDateString() === today.toDateString();
                    });
                  }
                  
                  return displayData.slice(0, 50).map((email, idx) => (
                    <div key={email.email_id || idx} style={{
                      padding: '1rem',
                      background: 'rgba(248, 250, 252, 0.05)',
                      borderRadius: '0.75rem',
                      border: '1px solid rgba(71, 85, 105, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      setSelectedDetailCard(null);
                      handleEmailClick(email);
                    }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Email ID</label>
                          <p style={{ fontFamily: 'monospace', color: '#3b82f6', fontWeight: '600', fontSize: '1rem', margin: 0 }}>{email.email_id}</p>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Priority</label>
                          <span style={
                            email.prediction_priority_value === "High"
                              ? { ...priorityHighStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                              : email.prediction_priority_value === "Normal"
                              ? { ...priorityNormalStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                              : { ...priorityLowStyle, fontSize: '0.875rem', padding: '0.25rem 0.75rem' }
                          }>
                            {email.prediction_priority_value}
                          </span>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>A Number</label>
                          <p style={{ fontFamily: 'monospace', color: '#3b82f6', fontSize: '1rem', margin: 0 }}>{email.case_number || "N/A"}</p>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Date</label>
                          <p style={{ fontSize: '1rem', fontFamily: 'monospace', color: '#e2e8f0', margin: 0 }}>
                            {email.classification_timestamp ? formatDate(email.classification_timestamp, "MM-DD HH:mm") : "-"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Subject</label>
                        <p style={{ fontSize: '1.125rem', fontWeight: '500', color: '#f1f5f9', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</p>
                      </div>
                      <div style={{ marginTop: '0.75rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Name</label>
                        <p style={{ fontSize: '1rem', color: '#e2e8f0', margin: 0 }}>{email.name || "Unknown"}</p>
                      </div>
                    </div>
                  ));
                })()}
                
                {(() => {
                  let totalCount = 0;
                  if (selectedDetailCard === 'total') totalCount = filteredData.length;
                  else if (selectedDetailCard === 'high-priority') totalCount = enhancedMetrics.highPriority;
                  else if (selectedDetailCard === 'active-cases') totalCount = enhancedMetrics.activeCases;
                  else if (selectedDetailCard === 'today') totalCount = enhancedMetrics.todayCount;
                  
                  if (totalCount > 50) {
                    return (
                      <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280', fontSize: '1.125rem' }}>
                        Showing first 50 of {totalCount.toLocaleString()} emails for performance
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Email Detail Modal */}
      {selectedEmail && (
        <EmailDetail
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
}

/* ------------------- Enhanced Style Objects ------------------- */
const containerStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #475569 75%, #64748b 100%)',
  color: '#f8fafc',
  position: 'relative'
};

const headerStyle = {
  borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
  background: 'rgba(15, 23, 42, 0.95)',
  backdropFilter: 'blur(16px)',
  position: 'sticky',
  top: 0,
  zIndex: 30,
  boxShadow: '0 8px 32px -8px rgba(0, 0, 0, 0.4)'
};

const headerContentStyle = {
  padding: '1.5rem'
};

const titleStyle = {
  fontSize: '2rem',
  fontWeight: '700',
  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  margin: 0,
  letterSpacing: '-0.025em'
};

const statusStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  fontSize: '1rem',
  color: '#e2e8f0',
  background: 'rgba(51, 65, 85, 0.8)',
  padding: '0.75rem 1rem',
  borderRadius: '0.75rem',
  border: '1px solid rgba(71, 85, 105, 0.4)',
  boxShadow: '0 4px 15px -4px rgba(0, 0, 0, 0.3)'
};

const statusDotStyle = {
  width: '0.5rem',
  height: '0.5rem',
  background: '#22d3ee',
  borderRadius: '50%',
  animation: 'pulse 2s infinite',
  boxShadow: '0 0 8px rgba(34, 211, 238, 0.6)'
};

const cardStyle = {
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.9) 100%)',
  borderRadius: '1.25rem',
  boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.4), 0 8px 32px -8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(148, 163, 184, 0.1)',
  border: '1px solid rgba(71, 85, 105, 0.3)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  backdropFilter: 'blur(16px)',
  position: 'relative'
};

const cardHeaderStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)',
  position: 'relative'
};

const metricCardBlue = {
  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.8) 50%, rgba(29, 78, 216, 0.9) 100%)',
  border: '2px solid rgba(96, 165, 250, 0.4)',
  transform: 'translateY(0)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 20px 40px -12px rgba(59, 130, 246, 0.4), 0 8px 25px -8px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(147, 197, 253, 0.2)',
  position: 'relative',
  overflow: 'hidden'
};

const metricCardRed = {
  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.8) 50%, rgba(185, 28, 28, 0.9) 100%)',
  border: '2px solid rgba(248, 113, 113, 0.4)',
  boxShadow: '0 20px 40px -12px rgba(239, 68, 68, 0.4), 0 8px 25px -8px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(252, 165, 165, 0.2)',
  position: 'relative',
  overflow: 'hidden'
};

const metricCardGreen = {
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.8) 50%, rgba(4, 120, 87, 0.9) 100%)',
  border: '2px solid rgba(52, 211, 153, 0.4)',
  boxShadow: '0 20px 40px -12px rgba(16, 185, 129, 0.4), 0 8px 25px -8px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(110, 231, 183, 0.2)',
  position: 'relative',
  overflow: 'hidden'
};

const metricCardPurple = {
  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.8) 50%, rgba(109, 40, 217, 0.9) 100%)',
  border: '2px solid rgba(167, 139, 250, 0.4)',
  boxShadow: '0 20px 40px -12px rgba(139, 92, 246, 0.4), 0 8px 25px -8px rgba(139, 92, 246, 0.3), inset 0 1px 0 rgba(196, 181, 253, 0.2)',
  position: 'relative',
  overflow: 'hidden'
};

const chartHeaderBlue = {
  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%)',
  borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
  padding: '1.5rem',
  position: 'relative'
};

const primaryButtonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '0.75rem',
  border: 'none',
  cursor: 'pointer',
  fontWeight: '600',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
  color: 'white',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '1rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 25px -8px rgba(59, 130, 246, 0.5), 0 4px 15px -4px rgba(59, 130, 246, 0.3)',
  minHeight: '48px',
  position: 'relative',
  overflow: 'hidden'
};

const outlineButtonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '0.75rem',
  border: '1px solid rgba(96, 165, 250, 0.4)',
  cursor: 'pointer',
  fontWeight: '600',
  background: 'rgba(30, 41, 59, 0.6)',
  color: '#93c5fd',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '1rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: '48px',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 4px 15px -4px rgba(0, 0, 0, 0.2)'
};

const inputStyle = {
  padding: '1rem',
  border: '1px solid rgba(71, 85, 105, 0.4)',
  borderRadius: '0.75rem',
  fontSize: '1rem',
  width: '100%',
  background: 'rgba(30, 41, 59, 0.6)',
  color: '#f8fafc',
  transition: 'all 0.3s ease',
  minHeight: '48px',
  backdropFilter: 'blur(8px)',
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
};

const selectStyle = {
  padding: '1rem',
  border: '1px solid rgba(71, 85, 105, 0.4)',
  borderRadius: '0.75rem',
  fontSize: '1rem',
  background: 'rgba(30, 41, 59, 0.6)',
  color: '#f8fafc',
  cursor: 'pointer',
  minHeight: '48px',
  backdropFilter: 'blur(8px)',
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#e2e8f0',
  display: 'block',
  marginBottom: '0.5rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const tableHeaderStyle = {
  background: 'linear-gradient(135deg, rgba(51, 65, 85, 0.8) 0%, rgba(71, 85, 105, 0.6) 100%)',
  backdropFilter: 'blur(8px)'
};

const thStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontWeight: '700',
  color: '#f1f5f9',
  fontSize: '1rem',
  letterSpacing: '0.05em',
  textTransform: 'uppercase'
};

const enhancedTableRowStyle = {
  transition: 'all 0.3s ease',
  borderBottom: '1px solid rgba(71, 85, 105, 0.2)',
  background: 'rgba(30, 41, 59, 0.3)',
  backdropFilter: 'blur(8px)'
};

const tdStyle = {
  padding: '1rem',
  borderTop: '1px solid rgba(71, 85, 105, 0.2)',
  fontSize: '1rem',
  verticalAlign: 'top',
  color: '#e2e8f0'
};

const linkButtonStyle = {
  background: 'transparent',
  color: '#60a5fa',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'monospace',
  fontWeight: '600',
  textDecoration: 'none',
  padding: '0.25rem 0.5rem',
  fontSize: '1rem',
  minHeight: 'auto',
  borderRadius: '0.375rem',
  transition: 'all 0.2s ease',
  position: 'relative'
};

const priorityHighStyle = {
  background: 'linear-gradient(to right, #ef4444, #dc2626)',
  color: 'white',
  padding: '0.375rem 1rem',
  borderRadius: '9999px',
  fontSize: '0.875rem',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  animation: 'pulse 2s infinite',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
};

const priorityNormalStyle = {
  background: 'linear-gradient(to right, #10b981, #059669)',
  color: 'white',
  padding: '0.375rem 1rem',
  borderRadius: '9999px',
  fontSize: '0.875rem',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
};

const priorityLowStyle = {
  background: 'linear-gradient(to right, #9ca3af, #6b7280)',
  color: 'white',
  padding: '0.375rem 1rem',
  borderRadius: '9999px',
  fontSize: '0.875rem',
  fontWeight: '600',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.8)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  padding: '1rem',
  animation: 'fadeIn 0.4s ease'
};

const modalContentStyle = {
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(51, 65, 85, 0.95) 100%)',
  borderRadius: '1.25rem',
  boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.6), 0 20px 40px -12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(148, 163, 184, 0.1)',
  animation: 'slideUp 0.4s ease',
  overflow: 'hidden',
  width: '100%',
  maxHeight: '90vh',
  border: '1px solid rgba(71, 85, 105, 0.3)',
  backdropFilter: 'blur(20px)'
};

const modalHeaderStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid rgba(71, 85, 105, 0.3)',
  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.15) 100%)',
  position: 'relative'
};

const modalBodyStyle = {
  padding: '1.5rem',
  maxHeight: '70vh',
  overflowY: 'auto'
};

const closeButtonStyle = {
  background: 'rgba(51, 65, 85, 0.6)',
  color: '#e2e8f0',
  border: '1px solid rgba(71, 85, 105, 0.4)',
  cursor: 'pointer',
  padding: '0.75rem',
  borderRadius: '0.75rem',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(8px)',
  boxShadow: '0 4px 15px -4px rgba(0, 0, 0, 0.2)'
};

const bodyTextStyle = {
  color: '#cbd5e1',
  background: 'rgba(30, 41, 59, 0.6)',
  padding: '1rem',
  borderRadius: '0.75rem',
  maxHeight: '8rem',
  overflowY: 'auto',
  fontSize: '1rem',
  lineHeight: '1.6',
  border: '1px solid rgba(71, 85, 105, 0.3)',
  backdropFilter: 'blur(8px)',
  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)'
};

/* Mobile-Responsive CSS Styles */
const mobileResponsiveCssStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #475569 75%, #64748b 100%);
    min-height: 100vh;
    color: #f8fafc;
    font-size: 16px;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  @media (max-width: 768px) {
    body {
      font-size: 14px;
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(1rem);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 480px) {
    .modal-content {
      margin: 0.5rem;
      max-width: calc(100vw - 1rem);
      max-height: calc(100vh - 1rem);
    }
  }

  ::-webkit-scrollbar {
    width: 12px;
    height: 12px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  button, input, select {
    min-height: 44px;
    touch-action: manipulation;
  }

  @media (max-width: 640px) {
    button, input, select {
      font-size: 16px;
    }
    
    .metric-cards {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 0.75rem !important;
    }
    
    .status-text {
      display: none !important;
    }
  }

  @media (max-width: 480px) {
    .metric-cards {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 0.5rem !important;
    }
  }

  .filter-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
  }

  @media (max-width: 768px) {
    .filter-grid {
      grid-template-columns: 1fr !important;
      gap: 0.75rem !important;
    }

    .charts-section {
      grid-template-columns: 1fr !important;
      gap: 1rem !important;
    }

    .timeline-chart {
      grid-column: 1 / -1 !important;
    }
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    .charts-section {
      grid-template-columns: 1fr !important;
      gap: 1.5rem !important;
    }

    .timeline-chart {
      grid-column: 1 / -1 !important;
    }
  }

  @media (min-width: 1025px) {
    .charts-section {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 1.5rem !important;
    }

    .timeline-chart {
      grid-column: 1 / -1 !important;
    }
  }
`;
